/**
 * @fileoverview Dashboard data provider component for centralized data fetching.
 * @module shared/providers/DashboardDataProvider
 */

import { ReactElement, ReactNode, useMemo, useContext, useCallback } from 'react'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  DashboardDataContext,
  DashboardDataContextType,
} from '@/shared/context/DashboardDataContext'
import { FilterContext, FilterContextType } from '@/shared/context/FilterContext'
import {
  statisticsService,
  UnifiedStatisticsFilterDTO,
  TimeAggregation,
  StatisticsDimensionGroup,
} from '@/lib/statisticsService'
import { prefixWithOrg, queryKeys } from '@/shared/utils/queryKeys'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'

/**
 * Props for DashboardDataProvider component.
 */
interface DashboardDataProviderProps {
  /** Child components to receive dashboard data context */
  children: ReactNode
  /** Optional filter values to override context (useful for keeping old data during transitions) */
  filterOverride?: Partial<FilterContextType>
}

/**
 * Provider component that manages centralized dashboard data fetching.
 *
 * @component
 * @example
 * ```tsx
 * <DashboardDataProvider>
 *   <Dashboard />
 * </DashboardDataProvider>
 * ```
 */
export function DashboardDataProvider({
  children,
  filterOverride,
}: DashboardDataProviderProps): ReactElement {
  const filterContext = useContext(FilterContext)
  const { activeOrgId } = useActiveOrg()
  const queryClient = useQueryClient()

  if (!filterContext) {
    throw new Error('DashboardDataProvider must be used within FilterProvider')
  }

  const startDate = filterOverride?.startDate ?? filterContext.startDate
  const endDate = filterOverride?.endDate ?? filterContext.endDate
  const aggregation = filterOverride?.aggregation ?? filterContext.aggregation
  const countries = filterOverride?.countries ?? filterContext.countries
  const skus = filterOverride?.skus ?? filterContext.skus
  const tags = filterOverride?.tags ?? filterContext.tags
  const profitBasis = filterOverride?.profitBasis ?? filterContext.profitBasis

  // Create filter object and time aggregation from context
  const { filter, timeAggregation } = useMemo(() => {
    const filterResult: UnifiedStatisticsFilterDTO = {}

    if (startDate) {
      filterResult.startDate = toDateOnlyStringLocal(startDate)
    }
    if (endDate) {
      filterResult.endDate = toDateOnlyStringLocal(endDate)
    }
    if (countries.length > 0) {
      filterResult.countries = countries
    }
    if (skus.length > 0) {
      filterResult.skus = skus
    }
    if (tags.length > 0) {
      filterResult.tags = tags
    }

    // Map frontend aggregation to API format
    let timeAggregationResult: TimeAggregation = 'MONTHLY' // default
    if (aggregation) {
      const aggregationMap = {
        Daily: 'DAILY',
        Weekly: 'WEEKLY',
        Monthly: 'MONTHLY',
        Quarterly: 'QUARTERLY',
        Yearly: 'YEARLY',
      } as const
      timeAggregationResult = aggregationMap[aggregation]
    }

    return {
      filter: filterResult,
      timeAggregation: timeAggregationResult,
    }
  }, [startDate, endDate, aggregation, countries, skus, tags])

  // Query for sales statistics with TIME dimension
  const salesTimeQuery = useQuery({
    // Apply allocation ONLY for TIME query when profitBasis is OC
    // Note: queryKey must include includeAllocated/allocation to scope cache correctly
    queryKey: activeOrgId
      ? prefixWithOrg(
          activeOrgId as unknown as string,
          queryKeys.salesStatistics(
            profitBasis === 'OC'
              ? { ...filter, includeAllocated: true, allocation: { strategy: 'TIME_ONLY' } }
              : filter,
            ['TIME'],
            timeAggregation
          )
        )
      : queryKeys.salesStatistics(
          profitBasis === 'OC'
            ? { ...filter, includeAllocated: true, allocation: { strategy: 'TIME_ONLY' } }
            : filter,
          ['TIME'],
          timeAggregation
        ),
    queryFn: () =>
      statisticsService.getSalesStatistics(
        profitBasis === 'OC'
          ? { ...filter, includeAllocated: true, allocation: { strategy: 'TIME_ONLY' } }
          : filter,
        ['TIME'],
        timeAggregation
      ),
    enabled: Boolean(activeOrgId && startDate && endDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: keepPreviousData,
  })

  // Queries are gated on activeOrgId via `enabled` flags

  // Query for sales statistics with COUNTRY+PRODUCT dimensions
  // Note: Required by ProfitByCountry on initial dashboard render
  const salesProductQuery = useQuery({
    queryKey: activeOrgId
      ? prefixWithOrg(
          activeOrgId as unknown as string,
          queryKeys.salesStatistics(filter, ['COUNTRY', 'PRODUCT'])
        )
      : queryKeys.salesStatistics(filter, ['COUNTRY', 'PRODUCT']),
    queryFn: () => statisticsService.getSalesStatistics(filter, ['COUNTRY', 'PRODUCT']),
    enabled: Boolean(activeOrgId && startDate && endDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: keepPreviousData,
  })

  // Query for sales statistics with PRODUCT dimension only (default view - no country breakdown)
  const salesProductOnlyQuery = useQuery({
    queryKey: activeOrgId
      ? prefixWithOrg(
          activeOrgId as unknown as string,
          queryKeys.salesStatistics(filter, ['PRODUCT'])
        )
      : queryKeys.salesStatistics(filter, ['PRODUCT']),
    queryFn: () => statisticsService.getSalesStatistics(filter, ['PRODUCT']),
    enabled: Boolean(activeOrgId && startDate && endDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: keepPreviousData,
  })

  // Prefetch function for country+product query (called on hover/idle)
  const prefetchCountryProductQuery = useCallback(() => {
    if (!activeOrgId || !startDate || !endDate) return

    const queryKey = prefixWithOrg(
      activeOrgId as unknown as string,
      queryKeys.salesStatistics(filter, ['COUNTRY', 'PRODUCT'])
    )

    queryClient.prefetchQuery({
      queryKey,
      queryFn: () => statisticsService.getSalesStatistics(filter, ['COUNTRY', 'PRODUCT']),
      staleTime: 5 * 60 * 1000,
    })
  }, [activeOrgId, startDate, endDate, filter, queryClient])

  // Query for variants list (independent of filters)
  const variantsQuery = useQuery({
    queryKey: activeOrgId
      ? prefixWithOrg(activeOrgId as unknown as string, queryKeys.variants())
      : queryKeys.variants(),
    queryFn: () => statisticsService.getAllVariants(),
    enabled: Boolean(activeOrgId),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    placeholderData: keepPreviousData,
  })

  // Compute combined states
  const isLoading = salesTimeQuery.isLoading || salesProductQuery.isLoading || salesProductOnlyQuery.isLoading || variantsQuery.isLoading
  const error =
    salesTimeQuery.error?.message ||
    salesProductOnlyQuery.error?.message ||
    salesProductQuery.error?.message ||
    variantsQuery.error?.message ||
    null

  const productGroups = useMemo<Map<string, StatisticsDimensionGroup>>(() => {
    const map = new Map<string, StatisticsDimensionGroup>()

    const merge = (
      dimensionGroups?: Record<string, Record<string, StatisticsDimensionGroup>>
    ): void => {
      const productGroupsForDimension = dimensionGroups?.product
      if (!productGroupsForDimension) {
        return
      }

      for (const [key, metadata] of Object.entries(productGroupsForDimension)) {
        map.set(key, metadata)
      }
    }

    merge(salesTimeQuery.data?.metadata?.dimensionGroups)
    merge(salesProductOnlyQuery.data?.metadata?.dimensionGroups)
    merge(salesProductQuery.data?.metadata?.dimensionGroups)

    return map
  }, [
    salesTimeQuery.data?.metadata?.dimensionGroups,
    salesProductOnlyQuery.data?.metadata?.dimensionGroups,
    salesProductQuery.data?.metadata?.dimensionGroups,
  ])

  // Create context value with new unified structure
  const value: DashboardDataContextType = useMemo(
    () => ({
      // New unified queries (primary)
      salesTimeQuery,
      salesProductQuery,
      salesProductOnlyQuery,
      variantsQuery,
      isLoading,
      error,
      productGroups,
      prefetchCountryProductQuery,
      // Legacy queries for backward compatibility (aliases)
      ecommerceQuery: salesTimeQuery,
      productsQuery: salesProductQuery,
      skusQuery: variantsQuery,
    }),
    [salesTimeQuery, salesProductQuery, salesProductOnlyQuery, variantsQuery, isLoading, error, productGroups, prefetchCountryProductQuery]
  )

  return <DashboardDataContext.Provider value={value}>{children}</DashboardDataContext.Provider>
}
